import { useContext, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import UserContext from "../../context/user";
import Question from "../question";
import useCartRequestUpdateMutation from "../../hooks/mutations/useCartRequestUpdateMutation";
import CartContext from "../../context/cart";
import { emailRegex, getSameEmailRegex } from "../../utils/regex";

const PickUpData = ({ setTempCartToSubmit }) => {
  const { userProfile } = useContext(UserContext);
  const {
    labels,
    survey,
    formData,
    setFormData,
    partial,
    campaign,
    cartRequest,
    appLang,
    handleNext,
  } = useContext(CartContext);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  const section = useMemo(() => survey?.definition?.sections?.[3], [survey]);

  const initializedFormData = useRef();

  const formWatch = watch();

  useEffect(() => {
    if (initializedFormData.current) {
      reset(formData);
    }
  }, [reset, formData]);

  useEffect(() => {
    if (!initializedFormData.current && section?.questions && userProfile) {
      initializedFormData.current = true;
      const initializedFields = {
        [section.questions?.[0]?.id]: userProfile?.firstName,
        [section.questions?.[1]?.id]: userProfile?.lastName,
        ...(cartRequest?.welfareEmail
          ? {
              [section.questions?.[3]?.id]: cartRequest.welfareEmail,
              [section.questions?.[4]?.id]: cartRequest.welfareEmail,
            }
          : {}),
      };
      let cartAssignedFields = {};
      if (cartRequest?.defaultcartAssigned) {
        cartAssignedFields = {
          [survey.definition.sections[0].questions[0].id]: cartRequest.contentId,
          [survey.definition.sections[1].questions[0].id]: "placeholder-assignedcart",
          [survey.definition.sections[1].questions[1].id]: "placeholder-assignedcart",
          [survey.definition.sections[1].questions[2].id]:
            survey.definition.sections[1].questions[2].answers[0].id,
        };
      }
      setFormData((current) => ({ ...current, ...initializedFields, ...cartAssignedFields }));
    }
  }, [userProfile, section, setFormData, cartRequest, survey]);

  const { mutateAsync: updateCartRequest } = useCartRequestUpdateMutation();

  const updateCartRequestFromValues = async (values) => {
    const updateCartParams = {
      campaignId: campaign?.id,
      cartRequest,
      survey,
      formData,
      values,
      partial,
      email: values[section.questions[3].id],
      phone: values[section.questions[2].id],
    };
    if (cartRequest?.status === "BOOKED") {
      // salva i parametri del nuovo booking in uno stato di appoggio

      // occorre modificare SOLO questi parametri del contesto, perché sono quelli che vengono poi letti dalla mutation quando viene fatta la chiamata API.
      // la mutation aggiorna anche il cart request ad ogni step, ma non ha senso farlo qui
      setTempCartToSubmit(updateCartParams);
      handleNext();
      setFormData((old) => {
        return {
          ...old,
          ...values,
        };
      });
      return;
    }

    await updateCartRequest(updateCartParams);
  };

  const onSubmit = async (values) => {
    updateCartRequestFromValues(values);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form" key={section.id} noValidate>
      {section.questions.map((question, i) => (
        <Question
          question={question}
          key={question.id}
          register={register}
          error={errors[`${question.id}`]}
          touched={touchedFields[`${question.id}`]}
          isDirty={dirtyFields[`${question.id}`]}
          control={control}
          trigger={trigger}
          readOnlyMode={i < 2}
          labels={labels}
          pattern={
            i === 3
              ? emailRegex
              : i === 4
              ? getSameEmailRegex(formWatch[section.questions[3].id])
              : null
          }
          customPatternErrorMsg={
            i === 3
              ? labels?.WELFARE_NOT_VALID_EMAIL || "The inserted email is not valid"
              : i === 4
              ? labels?.WELFARE_NOT_CORRESPONDING_EMAIL || "The inserted email does not correspond"
              : null
          }
          attachments={survey?.attachments}
          comment={
            i === 2
              ? {
                  message:
                    labels?.WELFARE_CART_PHONE_INPUT_DESCRIPTION ||
                    "Insert your phone number in order to contact you during the delivery if needed.",
                }
              : null
          }
          locale={appLang?.value}
          trim={[3, 4].includes(i)}
        />
      ))}
      <div className="form__buttons-wrapper">
        <div className="form__buttons form__buttons--center">
          <button className="button primary form__submit">
            {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default PickUpData;
